/* eslint-disable no-control-regex */
import React, { ReactNode } from 'react';
import { string } from 'yup';

import { useTranslate } from '@travel/translation';
import { hasAtLeastOneValue, isAllKeysHaveValue, isNotNullish } from '@travel/utils';

import { getMaxLength } from 'constants/languageMultipliers';
import { TYPE as PROVIDER_CONTACT_TYPE } from 'constants/providerInfo';

import { Translate } from 'core/translate';
import { BIN_ARRAY_MAX_LENGTH } from './promotion';

/**
 * Useful links
 * https://github.com/jquense/yup#string
 * https://runkit.com/570c52590a85f71200eb09ba/yup
 */

/**
 * validate removing leading and trailing whitespace
 * please add this to the head of 'validationRules' to validate trimmed value.
 */
export const TRIM = {
  type: 'trim',
  params: [true],
};

/**
 * required: true
 */
export const REQUIRED = {
  type: 'required',
  params: [useTranslate({ id: 'Extranet_Error.Other.Missing_Mandatory' })],
};

export const REQUIRED_SELECT_VALUE = {
  type: 'required',
  params: [useTranslate({ id: 'Extranet_Error.Select.Item' })],
};

export const REQUIRED_PAYMENT_METHOD = {
  type: 'required',
  params: [useTranslate({ id: 'Extranet_Error.Select.Payment_Method' })],
};

export const REQUIRED_DESIGNATION = {
  type: 'required',
  params: [useTranslate({ id: 'Extranet_Error.Select.Designation' })],
};

export const REQUIRED_RADIO_GROUP = {
  type: 'required',
  params: [useTranslate({ id: 'Extranet_Error.Select.Option' })],
};

export const REQUIRED_PASSWORD = {
  type: 'required',
  params: [useTranslate({ id: 'Extranet_Error.Enter.Password' })],
};

export const REQUIRED_SSP_PROVIDERS_APPLICABLE_SETTINGS = {
  type: 'required',
  params: [
    useTranslate({ id: 'Extranet_Error.SP.Applicable_Settings.Select_Providers_And_Rate_Plans' }),
  ],
};

export const REQUIRED_TERMINAL = (isInternal?: boolean) => {
  return {
    type: 'required',
    params: [
      useTranslate({
        id: isInternal
          ? 'Provider_Management.Edit.Nearby_Terminal.Notice.Terminal'
          : 'Provider_Information_Management.Detail.Nearby_Terminal.Notice.Terminal',
      }),
    ],
  };
};

export const REQUIRED_TRANSPORTATION_MODE = (isInternal?: boolean) => {
  return {
    type: 'required',
    params: [
      useTranslate({
        id: isInternal
          ? 'Provider_Management.Edit.Nearby_Terminal.Notice.Method'
          : 'Provider_Information_Management.Detail.Nearby_Terminal.Notice.Method',
      }),
    ],
  };
};

export const REQUIRED_TIME_TO_REACH = (isInternal?: boolean) => {
  return {
    type: 'required',
    params: [
      useTranslate({
        id: isInternal
          ? 'Provider_Management.Edit.Nearby_Terminal.Notice.Time'
          : 'Provider_Information_Management.Detail.Nearby_Terminal.Notice.Time',
      }),
    ],
  };
};

export const PLACE_ANCESTORS = (length: number) => {
  return {
    type: 'min',
    params: [length, <Translate id="Extranet_Error.Place.Minimum_Selection" />],
  };
};

export const minlength = (length: number, message?: string) => {
  return {
    type: 'min',
    params: [
      length,
      message || (
        <Translate
          id="Extranet_Error.Enter.Min_Character"
          data={{ minimum_character_limit: length }}
        />
      ),
    ],
  };
};

export const maxlength = (
  length: number,
  shouldCalcLengh: boolean = false,
  inputLanguage: string = 'en-US',
  message?: string,
) => {
  let maxLength = length;

  // The maximum length of the characters is determined from the expected length in Japanese and the largest "Language Multiplier"
  // https://confluence.rakuten-it.com/confluence/pages/viewpage.action?pageId=2443498673
  if (shouldCalcLengh) {
    maxLength = getMaxLength(maxLength, inputLanguage);
  }

  return {
    type: 'max',
    params: [
      maxLength,
      message || (
        <Translate
          id="Extranet_Error.Enter.Max_Character"
          data={{ maximum_character_limit: maxLength }}
        />
      ),
    ],
  };
};

export const MIN_VALUE_TRANSLATE_ID = 'Extranet_Error.Enter.Value_Minimum';

export const minvalue = (value: number, message?: string) => {
  return {
    type: 'min',
    params: [
      value,
      message || <Translate id={MIN_VALUE_TRANSLATE_ID} data={{ minimum_value: value }} />,
    ],
  };
};

export const maxvalue = (value: number, message?: string) => {
  return {
    type: 'max',
    params: [
      value,
      message || (
        <Translate id="Extranet_Error.Enter.Value_Maximum" data={{ maximum_value: value }} />
      ),
    ],
  };
};

export const compareValue = (key: string, compareType: 'min' | 'max', message?: ReactNode) => ({
  type: 'when',
  params: [
    key,
    (valueOfKey: number, validator: any) => {
      if (isNotNullish(valueOfKey)) {
        return validator[compareType](
          valueOfKey,
          message || <Translate id="Extranet_Error.System_Logic.Total_Min" />,
        );
      }
      return validator;
    },
  ],
});

const ANY_LANGUAGE =
  'a-zA-Z0-9\\s+\u0041-\u005A\u0061-\u007A\u00AA\u00B5\u00BA\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0370-\u0374\u0376\u0377\u037A-\u037D\u0386\u0388-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u048A-\u0527\u0531-\u0556\u0559\u0561-\u0587\u05D0-\u05EA\u05F0-\u05F2\u0620-\u064A\u066E\u066F\u0671-\u06D3\u06D5\u06E5\u06E6\u06EE\u06EF\u06FA-\u06FC\u06FF\u0710\u0712-\u072F\u074D-\u07A5\u07B1\u07CA-\u07EA\u07F4\u07F5\u07FA\u0800-\u0815\u081A\u0824\u0828\u0840-\u0858\u08A0\u08A2-\u08AC\u0904-\u0939\u093D\u0950\u0958-\u0961\u0971-\u0977\u0979-\u097F\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BD\u09CE\u09DC\u09DD\u09DF-\u09E1\u09F0\u09F1\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A59-\u0A5C\u0A5E\u0A72-\u0A74\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABD\u0AD0\u0AE0\u0AE1\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3D\u0B5C\u0B5D\u0B5F-\u0B61\u0B71\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BD0\u0C05-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C33\u0C35-\u0C39\u0C3D\u0C58\u0C59\u0C60\u0C61\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBD\u0CDE\u0CE0\u0CE1\u0CF1\u0CF2\u0D05-\u0D0C\u0D0E-\u0D10\u0D12-\u0D3A\u0D3D\u0D4E\u0D60\u0D61\u0D7A-\u0D7F\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0E01-\u0E3A\u0E3F-\u0E5B\u0E81\u0E82\u0E84\u0E87\u0E88\u0E8A\u0E8D\u0E94-\u0E97\u0E99-\u0E9F\u0EA1-\u0EA3\u0EA5\u0EA7\u0EAA\u0EAB\u0EAD-\u0EB0\u0EB2\u0EB3\u0EBD\u0EC0-\u0EC4\u0EC6\u0EDC-\u0EDF\u0F00\u0F40-\u0F47\u0F49-\u0F6C\u0F88-\u0F8C\u1000-\u102A\u103F\u1050-\u1055\u105A-\u105D\u1061\u1065\u1066\u106E-\u1070\u1075-\u1081\u108E\u10A0-\u10C5\u10C7\u10CD\u10D0-\u10FA\u10FC-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u1380-\u138F\u13A0-\u13F4\u1401-\u166C\u166F-\u167F\u1681-\u169A\u16A0-\u16EA\u1700-\u170C\u170E-\u1711\u1720-\u1731\u1740-\u1751\u1760-\u176C\u176E-\u1770\u1780-\u17B3\u17D7\u17DC\u1820-\u1877\u1880-\u18A8\u18AA\u18B0-\u18F5\u1900-\u191C\u1950-\u196D\u1970-\u1974\u1980-\u19AB\u19C1-\u19C7\u1A00-\u1A16\u1A20-\u1A54\u1AA7\u1B05-\u1B33\u1B45-\u1B4B\u1B83-\u1BA0\u1BAE\u1BAF\u1BBA-\u1BE5\u1C00-\u1C23\u1C4D-\u1C4F\u1C5A-\u1C7D\u1CE9-\u1CEC\u1CEE-\u1CF1\u1CF5\u1CF6\u1D00-\u1DBF\u1E00-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u2071\u207F\u2090-\u209C\u2102\u2107\u210A-\u2113\u2115\u2119-\u211D\u2124\u2126\u2128\u212A-\u212D\u212F-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2183\u2184\u2C00-\u2C2E\u2C30-\u2C5E\u2C60-\u2CE4\u2CEB-\u2CEE\u2CF2\u2CF3\u2D00-\u2D25\u2D27\u2D2D\u2D30-\u2D67\u2D6F\u2D80-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2E2F\u3005\u3006\u3031-\u3035\u303B\u303C\u3041-\u3096\u309D-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312D\u3131-\u318E\u31A0-\u31BA\u31F0-\u31FF\u3400-\u4DB5\u4E00-\u9FCC\uA000-\uA48C\uA4D0-\uA4FD\uA500-\uA60C\uA610-\uA61F\uA62A\uA62B\uA640-\uA66E\uA67F-\uA697\uA6A0-\uA6E5\uA717-\uA71F\uA722-\uA788\uA78B-\uA78E\uA790-\uA793\uA7A0-\uA7AA\uA7F8-\uA801\uA803-\uA805\uA807-\uA80A\uA80C-\uA822\uA840-\uA873\uA882-\uA8B3\uA8F2-\uA8F7\uA8FB\uA90A-\uA925\uA930-\uA946\uA960-\uA97C\uA984-\uA9B2\uA9CF\uAA00-\uAA28\uAA40-\uAA42\uAA44-\uAA4B\uAA60-\uAA76\uAA7A\uAA80-\uAAAF\uAAB1\uAAB5\uAAB6\uAAB9-\uAABD\uAAC0\uAAC2\uAADB-\uAADD\uAAE0-\uAAEA\uAAF2-\uAAF4\uAB01-\uAB06\uAB09-\uAB0E\uAB11-\uAB16\uAB20-\uAB26\uAB28-\uAB2E\uABC0-\uABE2\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uF900-\uFA6D\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D\uFB1F-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE70-\uFE74\uFE76-\uFEFC\uFF10-\uFF19\uFF21-\uFF3A\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC';

/**
 * input Restrictions: a-z, A-Z, 0-9. or characters in any language
 * placeholder: a-z, A-Z, 0-9
 */
export const ALPHA_NUMERIC = {
  type: 'matches',
  params: [
    new RegExp('^[' + ANY_LANGUAGE + ']*$'),
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Characters.Invalid' }),
  ],
};

// input restrictions defined in
// (beta2.0) https://confluence.rakuten-it.com/confluence/pages/viewpage.action?pageId=2345524700
// (beta2.0) https://confluence.rakuten-it.com/confluence/pages/viewpage.action?pageId=2299824055
// (beta3.0) https://confluence.rakuten-it.com/confluence/pages/viewpage.action?pageId=2376741451

/**
 * I-a-1(beta3.0)
 * input Restrictions: ",", "-", "(space)", a-z, A-Z, 0-9. or characters in any language
 * not allowed consecutive instances of ",", "-", and "(space)"
 * placeholder: a-z, A-Z, 0-9
 */
export const ADDRESS = {
  type: 'matches',
  params: [
    new RegExp(
      '^(?!.*[,]{2,})(?!.*[-]{2,})(?!.*[\u0020]{2,})(?!.*[\u3000]{2,})[\u002C\uFF0C\\-' +
        ANY_LANGUAGE +
        ']+$',
    ),
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Characters.Invalid' }),
  ],
};

/**
 * I-a-2(beta3.0)
 * input Restrictions: ', -, _, .,  (space), a-z, A-Z, 0-9. or characters in any language
 * not allowed consecutive instances of ', -, _, .,  (space)
 */
export const NAME = {
  type: 'matches',
  params: [
    new RegExp(
      "^(?!.*[']{2,})(?!.*[-]{2,})(?!.*[_]{2,})(?!.*[.]{2,})(?!.*[\u0020]{2,})(?!.*[\u3000]{2,})[-_." +
        ANY_LANGUAGE +
        ']+$',
    ),
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Characters.Symbol_Name' }),
  ],
};

/**
 * 1-a-4
 * input Restrictions: %, ., _, (space), a-z, A-Z, 0-9. or characters in any language
 * not allowed consecutive instances of "%", ".", "_" and "(space)"
 * placeholder: %, a-z, A-Z, 0-9
 */
export const FEATURE_NAME_REGEX = new RegExp(
  '^(?!.*[%]{2,})(?!.*[_]{2,})(?!.*[.]{2,})(?!.*[\u0020]{2,})(?!.*[\u3000]{2,})[%_.' +
    ANY_LANGUAGE +
    ']+$',
);

export const FEATURE_NAME = {
  type: 'matches',
  params: [
    FEATURE_NAME_REGEX,
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Characters.Invalid' }),
  ],
};

/**
 * 1-a-5(beta3.0)
 * input Restrictions: a-z, A-Z, 0-9
 * must include at least one capital letter, one lowercase letter, one number, and one symbol
 * numbers cannot be consecutive (e.g. 567)
 * letters cannot be the same three times in a row (e.g. aaa)
 * placeholder: a-z, A-Z, 0-9
 */
export const AVAILABLE_CHAR = /^[A-Za-z0-9!@#$%\^&*()+\-={}[\];:'<>_~,.?/¥]*$/;
// At least one capital word
export const ONE_CAPITAL = /^.*?[A-Z].*$/;
// At least one lowercase word
export const ONE_LOWERCASE = /^.*?[a-z].*$/;
// Must include at least a number
export const ONE_NUMBER = /^.*?[0-9].*$/;
// Must include at least one symbol
export const ONE_SYMBOL = /^.*?[!@#$%\^&*()+\-={}[\];:'<>_~,.?/¥].*$/;
// The number can't be consecutive (e.g. 567)
export const NOT_CONSECUTIVE_NUM = /^(?!.*(?:0123|1234|2345|3456|4567|5678|6789|9876|8765|7654|6543|5432|4321|3210)).*$/;
// characters can't be the same for 3 times in a row (e.g. aaa, bbB, 111, !!!)
export const NOT_THREETIMES_ALPHA = /^(?!.*(.)\1{2}).*$/i;
// password length should be 8 ~ 64
export const PWD_AVAILABLE_LENGTH = /^.{8,64}$/;
// password should not be contain [password, passwd, test, admin, administrator, sysadmin]
export const NOT_INVALID_WORDS = /^(?!.*(?:password|passwd|test|admin|administrator|sysadmin)).*$/;

const PWD_AVAILABLE_CHAR = {
  type: 'matches',
  params: [
    AVAILABLE_CHAR,
    useTranslate({ id: 'Extranet_Error.Enter.Password_Unsupported_Character' }),
  ],
};
const PWD_ONE_CAPITAL = {
  type: 'matches',
  params: [ONE_CAPITAL, useTranslate({ id: 'Extranet_Error.Enter.Password_Capital' })],
};
const PWD_ONE_LOWERCASE = {
  type: 'matches',
  params: [ONE_LOWERCASE, useTranslate({ id: 'Extranet_Error.Enter.Password_Lowercase' })],
};
const PWD_ONE_NUMBER = {
  type: 'matches',
  params: [ONE_NUMBER, useTranslate({ id: 'Extranet_Error.Enter.Password_One_Number' })],
};
const PWD_ONE_SYMBOLL = {
  type: 'matches',
  params: [ONE_SYMBOL, useTranslate({ id: 'Extranet_Error.Enter.Password_One_Symbol' })],
};
const PWD_NOT_CONSECUTIVE_NUM = {
  type: 'matches',
  params: [NOT_CONSECUTIVE_NUM, useTranslate({ id: 'Extranet_Error.Enter.Password_Consecutive' })],
};
const PWD_NOT_THREETIMES_ALPHA = {
  type: 'matches',
  params: [
    NOT_THREETIMES_ALPHA,
    useTranslate({ id: 'Extranet_Error.Enter.Password_Three_Alphabet' }),
  ],
};

const PWD_INVALID_WORDS = {
  type: 'matches',
  params: [NOT_INVALID_WORDS, useTranslate({ id: 'Extranet_Error.Enter.Password_illegal_word' })],
};

// Export for NewPasswordForm. TODO: [Refactor] Unify PasswordSection and NewPasswordForm
export const PASSWORD_REGEXES = [
  AVAILABLE_CHAR,
  ONE_CAPITAL,
  ONE_LOWERCASE,
  ONE_NUMBER,
  ONE_SYMBOL,
  NOT_CONSECUTIVE_NUM,
  NOT_THREETIMES_ALPHA,
  NOT_INVALID_WORDS, // invalid word check (password 、 passwd 、 test 、 admin 、 administrator 、 sysadmin)
  // (beta 3.0) (V1.1) Minimum and Maximum Value of the Numerical Value
  PWD_AVAILABLE_LENGTH, // TODO: use minLength/maxLength after refactoring on NewPasswordForm
];

// Export for PasswordSection. set here to unify restrictions.
export const PASSWORD_VALIDATIONS = [
  PWD_AVAILABLE_CHAR,
  PWD_ONE_CAPITAL,
  PWD_ONE_LOWERCASE,
  PWD_ONE_NUMBER,
  PWD_ONE_SYMBOLL,
  PWD_NOT_CONSECUTIVE_NUM,
  PWD_NOT_THREETIMES_ALPHA,
  PWD_INVALID_WORDS,
  // (beta 3.0) (V1.1) Minimum and Maximum Value of the Numerical Value
  minlength(8),
  maxlength(64),
];

/*
 * 1-a-6(beta3.0)
 * input Restrictions: _, a-z, A-Z, 0-9. or characters in any language
 * not allowed consecutive instances of "_"
 * placeholder: _, a-z, A-Z, 0-9
 */
export const ACCOUNT_ID = {
  type: 'matches',
  params: [
    /^(((?!_{2,})[a-zA-Z0-9_]){2,})?$/,
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Characters.Invalid' }),
  ],
};

/**
 * 1-a-7(Beta3.0)
 * input Restrictions: "?", "!", and "-", Alphanumerical, a-z, A-Z, 0-9
 * not allowed consecutive instances of "?", "!", and "-"
 * placeholder: ?, !, -, a-z, A-Z, 0-9
 */

const COUPON_CODE_VALID_CHARACTER = {
  type: 'matches',
  params: [
    /^[a-zA-Z0-9?!-]*$/,
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Characters.Symbol_Coupon_Code' }),
  ],
};

const CONPON_CODE_CONSECUTIVE_SYMBOL = {
  type: 'matches',
  params: [
    /^(?!.*[?]{2,})(?!.*[!]{2,})(?!.*[-]{2,}).*$/,
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Characters.Symbol_Consecutive' }),
  ],
};
export const COUPON_CODE = [COUPON_CODE_VALID_CHARACTER, CONPON_CODE_CONSECUTIVE_SYMBOL];

/**
 * 1-a-8(Beta3.0)
 * input Restrictions: ., _, a-z, A-Z, 0-9. or characters in any language
 * not allowed consecutive instances of "." and "_"
 * placeholder: ., _, a-z, A-Z, 0-9
 */
export const MANAGEMENT_NAME = {
  type: 'matches',
  params: [
    /^(?!.*[.]{2,})(?!.*[_]{2,})[a-zA-Z0-9_.]*$/,
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Characters.Invalid' }),
  ],
};

/**
 * Charactor limitation
 * 0000 - 001F, 007F and 0020 (SPACE) is NOT allowed
 */
export const NO_SPACE = {
  type: 'matches',
  params: [
    /^(?!.*[\u0000-\u001F\u007F\u0020]).*$/,
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Characters.Invalid' }),
  ],
};

/**
 * Charactor limitation
 * 0000 - 001F, 007F is NOT allowed
 */
export const NO_CTRL_CHAR = {
  type: 'matches',
  params: [
    /^(?!.*[\u0000-\u001F\u007F]).*$/,
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Characters.Invalid' }),
  ],
};

export const DESCRIPTION_REGEX = /^((?![\u00A9\u00AE\u203C\u2049\u2122\u2139\u2194-\u2199\u21A9-\u21AA\u231A-\u231B\u2328\u23CF\u23E9-\u23F3\u23F8-\u23FA\u24C2\u25AA-\u25AB\u25B6\u25C0\u25FB-\u25FE\u2600-\u2604\u260E\u2611\u2614-\u2615\u2618\u261D\u2620\u2622-\u2623\u2626\u262A\u262E-\u262F\u2638-\u263A\u2640\u2642\u2648-\u2653\u2660\u2663\u2665-\u2666\u2668\u267B\u267F\u2692-\u2697\u2699\u269B-\u269C\u26A0-\u26A1\u26AA-\u26AB\u26B0-\u26B1\u26BD-\u26BE\u26C4-\u26C5\u26C8\u26CE-\u26CF\u26D1\u26D3-\u26D4\u26E9-\u26EA\u26F0-\u26F5\u26F7-\u26FA\u26FD\u2702\u2705\u2708-\u270D\u270F\u2712\u2714\u2716\u271D\u2721\u2728\u2733-\u2734\u2744\u2747\u274C\u274E\u2753-\u2755\u2757\u2763-\u2764\u2795-\u2797\u27A1\u27B0\u27BF\u2934-\u2935\u2B05-\u2B07\u2B1B-\u2B1C\u2B50\u2B55\u3030\u303D\u3297\u3299]|(?:\uD83C[\uDC04\uDCCF\uDD70-\uDD71\uDD7E-\uDD7F\uDD8E\uDD91-\uDD9A\uDDE6-\uDDFF\uDE01-\uDE02\uDE1A\uDE2F\uDE32-\uDE3A\uDE50-\uDE51\uDF00-\uDF21\uDF24-\uDF93\uDF96-\uDF97\uDF99-\uDF9B\uDF9E-\uDFF0\uDFF3-\uDFF5\uDFF7-\uDFFF]|\uD83D[\uDC00-\uDCFD\uDCFF-\uDD3D\uDD49-\uDD4E\uDD50-\uDD67\uDD6F-\uDD70\uDD73-\uDD7A\uDD87\uDD8A-\uDD8D\uDD90\uDD95-\uDD96\uDDA4-\uDDA5\uDDA8\uDDB1-\uDDB2\uDDBC\uDDC2-\uDDC4\uDDD1-\uDDD3\uDDDC-\uDDDE\uDDE1\uDDE3\uDDE8\uDDEF\uDDF3\uDDFA-\uDE4F\uDE80-\uDEC5\uDECB-\uDED2\uDEE0-\uDEE5\uDEE9\uDEEB-\uDEEC\uDEF0\uDEF3-\uDEF6]|\uD83E[\uDD10-\uDD1E\uDD20-\uDD27\uDD30\uDD33-\uDD3A\uDD3C-\uDD3E\uDD40-\uDD45\uDD47-\uDD4B\uDD50-\uDD5E\uDD80-\uDD91\uDDC0])).)*$/gm;
/**
 * input Restrictions: Basic Latin (ASCII)
 * non Basic Latin (ASCII) is NOT allowed
 */
export const DESCRIPTION = {
  type: 'matches',
  params: [
    DESCRIPTION_REGEX,
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Characters.Invalid' }),
  ],
};

/**
 * input Restrictions: A number can't be zero
 */
export const NON_ZERO = {
  type: 'matches',
  params: [
    /^[1-9]\d*$/,
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Number_Digit.Cannot_Be_Zero' }),
  ],
};

export const NULLABLE = {
  type: 'nullable',
  params: [true],
};

// TODO Will be updated types and prams based on
// https://confluence.rakuten-it.com/confluence/display/EVEREST/PSD+-+Validation+Rule

export const WHITE_SPACE = {
  type: 'matches',
  params: [/( |　)+/, 'WHITE_SPACE'],
};

export const POSITIVE = {
  type: 'positive',
  params: [useTranslate({ id: 'Extranet_Error.Enter.Positive_Number' })],
};

export const INTEGER_ONLY_TRANSLATE_ID =
  'Extranet_Error.Input_Value_Errors.Number_Digit.Only_Integer';

export const INTEGER = {
  type: 'integer',
  params: [useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Number_Digit.Only_Integer' })],
};

export const DECIMAL_REGEX = /^(0|[1-9]\d*)$/;

export const DECIMAL_TRANSLATE_ID = 'Extranet_Error.Enter.Valid_Decimal';
export const DECIMAL = {
  type: 'matches',
  params: [DECIMAL_REGEX, useTranslate({ id: DECIMAL_TRANSLATE_ID })],
};

export const ALPHABET = {
  type: 'matches',
  params: [/^[a-zA-Z\s?!-]*$/, 'Please enter only characters'],
};

export const SPECIAL_CHAR = {
  type: 'matches',
  params: [/^[a-zA-Z0-9\s?!-]*$/, 'SPECIAL_CHAR'],
};

export const PROVIDER_LANG = {
  type: 'matches',
  params: [/ /, 'PROVIDER_LANG'],
};

export const PHONE_NUMBER_REGEX = /^(\+)?[0-9- \uFF0D][\d- \uFF0D]+[0-9]$/;

export const PHONE_NUMBER = {
  type: 'matches',
  params: [
    PHONE_NUMBER_REGEX,
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Number_Digit.Only_Numerical' }),
  ],
};

export const NUMERICAL_ONLY_REGEX = /^-?[0-9][0-9,.]*$/;
export const NUMERICAL_ONLY_ERROR_TRANSLATE_ID =
  'Extranet_Error.Input_Value_Errors.Number_Digit.Only_Numerical';

// allows negative numbers and decimals
export const NUMERICAL_ONLY = {
  type: 'matches',
  params: [NUMERICAL_ONLY_REGEX, useTranslate({ id: NUMERICAL_ONLY_ERROR_TRANSLATE_ID })],
};

// does not allow negative numbers and decimals
export const NUMBER = {
  type: 'matches',
  params: [/^[0-9]+$/, useTranslate({ id: NUMERICAL_ONLY_ERROR_TRANSLATE_ID })],
};

export const LATITUDE = {
  type: 'matches',
  params: [
    /^(\+|-)?(?:90(?:(?:\.0{1,18})?)|(?:[0-9])(?:(?:\.[0-9]{1,19})?)|(?:[1-8][0-9])(?:(?:\.[0-9]{1,18})?))$/,
    useTranslate({ id: 'Extranet_Error.Enter.Latitude' }),
  ],
};

export const LONGITUDE = {
  type: 'matches',
  params: [
    /^(\+|-)?(?:180(?:(?:\.0{1,17})?)|(?:[0-9])(?:(?:\.[0-9]{1,19})?)|(?:[1-9][0-9])(?:(?:\.[0-9]{1,18})?)|(?:1[0-7][0-9])(?:(?:\.[0-9]{1,17})?))$/,
    useTranslate({ id: 'Extranet_Error.Enter.Longitude' }),
  ],
};

export const IP_ADDRESS = {
  type: 'matches',
  params: [
    /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(\/\d{1,2})?$/,
    useTranslate({ id: 'Extranet_Error.Enter.IP_Address' }),
  ],
};

export const HTTP_HEADERS = {
  type: 'matches',
  params: [
    /^[a-zA-Z\-/]+:{1}[a-zA-Z0-9\-/]+$/,
    useTranslate({
      id: 'Distributor_Management.Create_Detail_Edit.HTTP_Header.Error.Valid_Format',
    }),
  ],
};

export const HTTPS_URL = {
  type: 'matches',
  params: [
    /(https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-/]))?/,
    useTranslate({ id: 'Extranet_Error.Enter.HTTPS_URL' }),
  ],
};

/**
 * 1-a-11(2.1.0)
 * input Restrictions: Alphanumerical, a-z, A-Z, 0-9. "-" and "_"	or characters in any language
 * not allowed consecutive instances of "_" or "-" allowed
 * placeholder: a-z, A-Z, 0-9
 */
export const PLACE_URL_NAME = {
  type: 'matches',
  params: [
    /^(((?!_{2,})(?!-{2,})[a-zA-Z0-9_-]){0,})?$/,
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Characters.Invalid' }),
  ],
};

// [TEQB-6861] use RFC5322 email definition instead of WHATWG HTML5 definition to migrate smoothly in RC2.0, 2021/2.
export const EMAIL_REGEXP = /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/;
export const EMAIL = {
  type: 'matches', // NOTE: don't use yup email regexp. use RFC5322 regexp
  params: [EMAIL_REGEXP, useTranslate({ id: 'Extranet_Error.Enter.Email' })],
};

export const REQUIRED_ON_ACTIVE = (value: boolean, message?: string) => {
  const mandatoryMessage = useTranslate({ id: 'Extranet_Error.Other.Missing_Mandatory' });
  if (value) {
    return {
      type: 'required',
      params: [message || mandatoryMessage],
    };
  }
  return {
    type: 'notRequired',
    params: [],
  };
};

export const REQUIRED_ON_ACTIVE_SELECTION = (value: boolean, message?: string) => {
  if (value) {
    return {
      type: 'required',
      params: [message || <Translate id="Extranet_Error.Select.Item" />],
    };
  }
  return {
    type: 'notRequired',
    params: [],
  };
};

export const INTEGER_CURRENCIES = ['KRW', 'IDR', 'JPY'];

export const INTEGER_ON_CURRENCY = (currency: string) => {
  return INTEGER_CURRENCIES.indexOf(currency) !== -1 ? INTEGER : DECIMAL;
};

/**
 * input restrictions [TEQB-4617]
 * not allowed Control Characters(\u0000 - \u0001F, \u007F)
 * not allowed half-width symbols (" ' ; \ ｰ ｡ ｢ ｣ ､ ･ ﾞ ﾟ < >), {0022 0027 003B 005C FF70 FF61 FF62 FF63 FF64 FF65 FF9E FF9F 003C 003E}
 * not allowed half-width katakana (\uFF66 - \uFF6F, \uFF71 - \uFF9D)
 */

export const INVALID_CONTROL_CHAR_REGEX = /^[^\u0000-\u001F\u007F]+$/;
const INVALID_CONTROL_CHAR = {
  type: 'matches',
  params: [
    INVALID_CONTROL_CHAR_REGEX,
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Characters.C0_Controls' }),
  ],
};

export const INVALID_SYMBOLS_REGEX = /^[^\u0022\u0027\u003B\u005C\uFF61-\uFF65\uFF70\uFF9E\uFF9F\u003C\u003E]+$/;
export const INVALID_SYMBOLS = {
  type: 'matches',
  params: [
    INVALID_SYMBOLS_REGEX,
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Characters.Symbol_Half_Width' }),
  ],
};
export const INVALID_KATAKANA_REGEX = /^[^\uFF66-\uFF6F\uFF71-\uFF9D]+$/;
export const INVALID_KATAKANA = {
  type: 'matches',
  params: [
    INVALID_KATAKANA_REGEX,
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Characters.Katakana_Half_Width' }),
  ],
};

export const NO_INVALID_CHAR = [INVALID_CONTROL_CHAR, INVALID_SYMBOLS, INVALID_KATAKANA];

export const INVALID_CHAR_REGEX = /^(?!.*[\u0000-\u001F\u007F\u0022\u0027\u003B\u005C\uFF61-\uFF65\uFF70\uFF9E\uFF9F\u003C\u003E\uFF66-\uFF6F\uFF71-\uFF9D]).*$/;

export const INVALID_SYMBOLS_FEATURE = {
  type: 'matches',
  params: [
    /^[^\u0022\u003B\u005C\uFF61-\uFF65\uFF70\uFF9E\uFF9F\u003C\u003E]+$/,
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Characters.Symbol_Half_Width' }),
  ],
};

export const NO_INVALID_CHAR_FEATURE = [
  INVALID_CONTROL_CHAR,
  INVALID_SYMBOLS_FEATURE,
  INVALID_KATAKANA,
];

/**
 * input restrictions for description[TEQB-4617]
 * not allowed Control Characters(\u0000-\u0009 \u000B-\u000C \u000E-\u001F, \u007F) 000A, 000D is line break
 * not allowed half-width symbols (" ' ; \ ｰ ｡ ｢ ｣ ､ ･ ﾞ ﾟ < >), {0022 0027 003B 005C FF70 FF61 FF62 FF63 FF64 FF65 FF9E FF9F 003C 003E}
 * not allowed half-width katakana (\uFF66 - \uFF6F, \uFF71 - \uFF9D)
 */
export const INVALID_CONTROL_CHAR_DESCRIPTION_REGEX = /^[^\u0000-\u0009\u000B\u000C\u000E-\u001F\u007F]+$/;
export const INVALID_CONTROL_CHAR_DESCRIPTION = {
  type: 'matches',
  params: [
    INVALID_CONTROL_CHAR_DESCRIPTION_REGEX,
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Characters.C0_Controls' }),
  ],
};

export const NO_INVALID_CHAR_DESCRIPTION = [
  INVALID_CONTROL_CHAR_DESCRIPTION,
  INVALID_SYMBOLS,
  INVALID_KATAKANA,
];

export const INVALID_CHAR_REGEX_DESCRIPTION = /^[^\u0000-\u0009\u000B\u000C\u000E-\u001F\u007F\u0022\u0027\u003B\u005C\uFF61-\uFF65\uFF70\uFF9E\uFF9F\u003C\u003E\uFF66-\uFF6F\uFF71-\uFF9D]+$/g;

/**
 * input restrictions any characters in any languages
 * not allowed consecutive instances of "_", "-", "." and "(space)"
 */
export const NO_CONSECUTIVE_CHARS_REGEX = /^(?!.*[-_.\u0020]{2}).*$/;
export const NO_CONSECUTIVE_CHARS = {
  type: 'matches',
  params: [
    NO_CONSECUTIVE_CHARS_REGEX,
    useTranslate({ id: 'Extranet_Error.Input_Value_Errors.Characters.Invalid' }),
  ],
};

export const RESERVATION_NOTE_MAX_LENGTH = 1400;

/** other utils */
export const SHOULD_BE_UNIQUE_IN_ARRAY = {
  type: 'uniqueInArray',
  params: [useTranslate({ id: 'Extranet_Error.Duplicate_Errors.Value' })],
};

export const HAS_AT_LEAST_ONE_VALUE = {
  type: 'test',
  params: [
    'atLeastOneValue',
    useTranslate({ id: 'Extranet_Error.Other.Missing_Mandatory' }),
    function(value: any) {
      if (!value || typeof value !== 'object') {
        return false;
      }
      return hasAtLeastOneValue(value);
    },
  ],
};

export const ALL_VALUES_IN_OBJECT_ARE_NEEDED = {
  type: 'test',
  params: [
    'allValuesInObjectAreNeeded',
    useTranslate({ id: 'Extranet_Error.Other.Missing_Mandatory' }),
    function(value: any) {
      if (!value || typeof value !== 'object') {
        return false;
      }
      return isAllKeysHaveValue(value);
    },
  ],
};

export const UNIQUE_ARRAY = {
  type: 'test',
  params: [
    'uniqueArray',
    useTranslate({ id: 'Extranet_Error.Duplicate_Errors.Value' }),
    (values: any) => new Set(values).size === values.length,
  ],
};

export const INVALID_PROVIDER_CONTACT = {
  type: 'when',
  params: [
    'type',
    (contactType: string, validator: any) => {
      if (contactType === PROVIDER_CONTACT_TYPE.EMAIL) {
        // [TEQB-6861] Only allow symbols and syntax rules within scope of RFC5322
        return validator[EMAIL.type](...EMAIL.params);
      } else {
        // [TEQB-6861] "No limitation; no syntax validation required"
        return validator;
      }
    },
  ],
};

export const needIfFlagIsNotChecked = (keyName: string, message?: string) => {
  return {
    type: 'when',
    params: [
      keyName,
      (flag: boolean, validator: any) => {
        // will check if applicablePlans is empty only if applyToAllProviders is false
        if (!flag) {
          return validator['required']([
            message || useTranslate({ id: 'Extranet_Error.Other.Missing_Mandatory' }),
          ]);
        } else {
          return validator;
        }
      },
    ],
  };
};

export const notRequiredIfFlagIsChecked = (keyName: string) => {
  return {
    type: 'when',
    params: [
      keyName,
      (flag: boolean, validator: any) => {
        // will ignore selectedPlans is empty only if applyForNewPlan is true
        if (flag) {
          return validator['notRequired']();
        } else {
          return validator;
        }
      },
    ],
  };
};

export const REQUIRE_MAX_CHILDREN = {
  type: 'when',
  params: [
    'minChildrenNumber',
    (minChildrenNumber: string, validator: any) => {
      if (isNotNullish(minChildrenNumber)) {
        return validator['required']([
          useTranslate({ id: 'Extranet_Error.Other.Missing_Mandatory' }),
        ]);
      } else {
        return validator;
      }
    },
  ],
};

export const OF_IP_ADDRESS = {
  type: 'of',
  params: [
    string()
      .required(useTranslate({ id: 'Extranet_Error.Other.Missing_Mandatory' }))
      .matches(
        /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(\/\d{1,2})?$/,
        useTranslate({ id: 'Extranet_Error.Enter.IP_Address' }),
      ),
  ],
};

export const OF_HTTP_HEADERS = {
  type: 'of',
  params: [
    string()
      .required(useTranslate({ id: 'Extranet_Error.Other.Missing_Mandatory' }))
      .matches(
        /^[a-zA-Z\-/]+:{1}[a-zA-Z0-9\-/]+$/,
        useTranslate({
          id: 'Distributor_Management.Create_Detail_Edit.HTTP_Header.Error.Valid_Format',
        }),
      ),
  ],
};

export const BIN_NUMBER_4_TO_8_DIGITS = /^[0-9]{4,8}$/;
export const BIN_NUMBER_VALID_SEPARATOR = /^[A-Za-z0-9]*$/;
export const BIN_NUMBER = (errorMessage: string, pattern: RegExp, validateLength?: number) => ({
  type: 'test',
  params: [
    'binNumbers',
    errorMessage,
    (arr: string[]) =>
      arr.every((val, idx) =>
        idx <= (validateLength ?? BIN_ARRAY_MAX_LENGTH) ? (val || '').match(pattern) : true,
      ),
  ],
});
export const requiredIfFieldIsChecked = (keyName: string, message?: string) => ({
  type: 'when',
  params: [
    keyName,
    (flag: boolean, validator: any) => {
      if (flag) {
        return validator['required']([
          message || useTranslate({ id: 'Extranet_Error.Other.Missing_Mandatory' }),
        ]);
      } else {
        return validator;
      }
    },
  ],
});
